/**
 * Save or share a Blob in a way that survives in-app WebViews.
 *
 * Three real-world environments we have to cope with:
 *
 *   1. iOS Safari / WKWebView
 *      Web Share API works reliably — files go to the OS share sheet
 *      (Save to Photos, Mail, Messages…).
 *
 *   2. Android — React Native WebView (the Angel Squad shell app)
 *      `blob:` URLs do NOT route through Android's DownloadManager, so the
 *      classic `<a download>` trick silently fails and the user is left
 *      with a toast claiming success and an empty Downloads folder.
 *      Fix: upload the blob to our relay endpoint and then navigate the
 *      WebView to the resulting `https://` URL — which IS something the
 *      shell's download listener can intercept. We also fire a
 *      `download_url` event through the native bridge so a future shell
 *      update can handle it explicitly via React Native APIs.
 *
 *   3. Desktop / mobile Chrome / unknown
 *      Try Web Share API first, then fall back to anchor download.
 *
 * Return shape (caller uses this to pick the toast):
 *   { shared: true }                              — OS share sheet succeeded
 *   { shared: false, cancelled: true }            — user cancelled the share sheet
 *   { shared: false, relayed: true,
 *     platform: "android-webview", url }          — uploaded + opened via relay
 *   { shared: false, downloaded: true,
 *     platform: "android" | "other" }             — anchor-tag download
 */
import { api } from "./api";
import { notifyNativeShell } from "./nativeBridge";

const isAndroid = () =>
    typeof navigator !== "undefined" && /android/i.test(navigator.userAgent || "");

const isInReactNativeWebView = () =>
    typeof window !== "undefined" && !!window.ReactNativeWebView;

/**
 * Upload the blob to the backend relay and navigate the WebView to the
 * returned https URL. Returns the relay URL on success or null on failure.
 */
async function relayThroughBackend(blob, filename, mimeType) {
    const form = new FormData();
    form.append("file", blob, filename);
    form.append("filename", filename);
    form.append("mimeType", mimeType || blob.type || "application/octet-stream");
    const { data } = await api.post("/share/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        // Larger files like framed prints can run a few MB
        maxContentLength: 20 * 1024 * 1024,
        maxBodyLength: 20 * 1024 * 1024,
    });
    if (!data?.url) return null;
    // The relay returns a path; make it absolute so the WebView treats it
    // as a navigation/download URL (not an in-app route).
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return base.replace(/\/$/, "") + data.url;
}

export async function saveOrShareBlob({ blob, filename, mimeType, title, text }) {
    if (!blob) throw new Error("Could not build the file.");
    const file = new File([blob], filename, { type: mimeType });
    const android = isAndroid();
    const rnWebView = isInReactNativeWebView();

    // ── Path 1: Android RN WebView shell ─────────────────────────────────
    // Skip Web Share + anchor entirely — both are silently broken here.
    // Upload to the relay and ask the shell (or the WebView's built-in
    // DownloadManager) to handle the resulting https URL.
    if (android && rnWebView) {
        try {
            const url = await relayThroughBackend(blob, filename, mimeType);
            if (url) {
                // Tell the native shell what's about to happen — a cooperating
                // shell can intercept this and call RN's file-system APIs
                // directly. Even a non-cooperating shell will see the
                // window.open below and route it through its DownloadListener.
                notifyNativeShell({
                    type: "download_url",
                    url,
                    filename,
                    mimeType: mimeType || blob.type,
                    method: "relay",
                    downloadedAt: new Date().toISOString(),
                    source: "myangelsquad.com",
                });
                // Open in a new context so the shell's
                // onShouldStartLoadWithRequest / setDownloadListener fires.
                // _blank is required — same-window navigation would replace
                // the portal with a half-rendered download response.
                window.open(url, "_blank");
                return { shared: false, relayed: true, platform: "android-webview", url };
            }
        } catch (e) {
            console.warn("share relay failed, falling back to anchor:", e);
            // fall through to the anchor approach below as a last resort
        }
    }

    // ── Path 2: iOS / desktop / mobile-Chrome — try Web Share ────────────
    // We still skip Web Share on Android (even outside RN WebView) because
    // mobile-Chrome's file-share path is patchy across OEMs; anchor
    // download is the more predictable outcome there.
    if (!android && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
            await navigator.share({
                files: [file],
                title: title || filename,
                ...(text ? { text } : {}),
            });
            notifyNativeShell({
                type: "download_url",
                url: null,
                filename,
                mimeType: mimeType || blob.type,
                method: "web-share",
                downloadedAt: new Date().toISOString(),
                source: "myangelsquad.com",
            });
            return { shared: true };
        } catch (e) {
            if (e && e.name === "AbortError") {
                return { shared: false, cancelled: true };
            }
            console.warn("Web Share failed, falling back to download:", e);
        }
    }

    // ── Path 3: anchor-tag download (final fallback) ─────────────────────
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 4000);
    notifyNativeShell({
        type: "download_url",
        url: null,
        filename,
        mimeType: mimeType || blob.type,
        method: "anchor",
        downloadedAt: new Date().toISOString(),
        source: "myangelsquad.com",
    });
    return { shared: false, downloaded: true, platform: android ? "android" : "other" };
}
